import { faBoxOpen, faPrint, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Filter from 'components/Bookings2/List/Filter/Filter';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import Button from 'components/UI/V3/Button/Button';
import SearchComponentPopup from 'components/UI/SearchComponentPopup';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { Option, Period } from '@spike/model';
import { ChangeEvent, FunctionComponent } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useMediaQuery } from 'react-responsive';
import RoomsFilter from '../Ops/filters/RoomsFilter';
import TasksTitlePortal from '../TasksTitlePortal';
import { TasksDatePicker } from '../TasksDatePicker';
import styles from './BelongingsHeader.module.scss';

interface BelongingsHeaderProps {
    search: string;
    datePeriod: Period & { name?: string };
    selectedMarketplaceIds: Array<number>;
    selectedRoomIds: Array<number>;
    belongingOptions: Array<Option<number>>;
    selectedBelongingIds: Array<number>;
    className?: string;
    onSearch?: (search: string) => void;
    onChangeDate?: (period: Period & { name?: string }) => void;
    onChangeMarketplaces?: (marketplaceIds: Array<number>) => void;
    onChangeRooms?: (roomIds: Array<number>) => void;
    onChangeBelongings?: (belongingIds: Array<number>) => void;
    onPrint?: () => void;
}

export const BelongingsHeader: FunctionComponent<BelongingsHeaderProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:960px)' });
    const debounced = useDebouncedCallback(value => props.onSearch?.(value), 500);
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const showMarketplaceFilter = organizationMarketplaces.length >= 2;
    const defaultMarketplaceIds = organizationMarketplaces
        .map(m => m.id)
        .filter((id): id is number => id !== undefined);

    const effectiveMarketplaceIds =
        props.selectedMarketplaceIds.length > 0 ? props.selectedMarketplaceIds : defaultMarketplaceIds;

    const calendarFilter = (
        <TasksDatePicker
            value={props.datePeriod}
            className={styles.datePicker}
            onChange={period => props.onChangeDate?.(period)}
        />
    );

    const locationsFilter = showMarketplaceFilter && (
        <OrganizationMarketplacesFilter
            selected={effectiveMarketplaceIds}
            onChangeSelection={ids => props.onChangeMarketplaces?.(ids)}
        />
    );

    const roomsFilter = (
        <RoomsFilter
            selected={props.selectedRoomIds}
            onChangeSelection={ids => props.onChangeRooms?.(ids)}
        />
    );

    const allItemsFilter = (
        <Filter
            showLabelOnMobile
            allLabel="All Items"
            quantityLabel="items"
            icon={faBoxOpen}
            options={props.belongingOptions}
            selectedOptions={props.belongingOptions.filter(option => props.selectedBelongingIds.includes(option.id))}
            onChangeSelection={selected => props.onChangeBelongings?.(selected.map(option => option.id))}
        />
    );

    const printButton = (
        <Button
            label=""
            type="button"
            size="large"
            onlyIcon
            leftIcon={faPrint}
            variant="black"
            onClick={() => props.onPrint?.()}
        />
    );

    if (isMobile) {
        return (
            <div className={clsx(styles.container, props.className)}>
                <TasksTitlePortal>
                    <SearchComponentPopup
                        flushIcon
                        title="Tasks"
                        placeholder="Search by clients or pets"
                        onSearch={text => debounced(text)}
                    />
                </TasksTitlePortal>

                <div className={styles.row}>
                    <div className={styles.mobileLeft}>
                        {allItemsFilter}
                        {calendarFilter}
                        {roomsFilter}
                        {locationsFilter}
                        {printButton}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={clsx(styles.container, props.className)}>
            <div className={styles.row}>
                <div className={styles.left}>
                    <Input
                        value={props.search}
                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                        placeholder="Search by clients or pets"
                        className={styles.search}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => debounced(e.target.value)}
                    />
                </div>
                <div className={styles.right}>
                    {locationsFilter}
                    {calendarFilter}
                    {roomsFilter}
                    {allItemsFilter}
                    {printButton}
                </div>
            </div>
        </div>
    );
};

export default BelongingsHeader;
